<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import arrowRightSmallIcon from '@/assets/icons/arrow-right-small.svg';
import exportIcon from '@/assets/icons/export.svg';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';
import { type SettingsLinkItem } from '@/views/SettingsView.types';

import { InnerLayoutButtonType } from '@/components/layouts/InnerLayout.types';
import InnerLayout from '@/components/layouts/InnerLayout.vue';
import UiIcon from '@/components/ui/UiIcon.vue';
import UiLink from '@/components/ui/UiLink.vue';

const { t } = useI18n();
const { replace } = useRouter();

const items = computed((): SettingsLinkItem[] =>
  [
    {
      name: t('legal.termsAndConditions'),
      href: 'https://holyheld.com/legal/terms-and-conditions'
    },
    {
      name: t('legal.fees'),
      href: 'https://holyheld.com/legal/fees'
    },
    {
      name: t('legal.privacyPolicy'),
      href: 'https://holyheld.com/legal/privacy-policy'
    },
    {
      name: t('legal.amlPolicy'),
      href: 'https://holyheld.com/legal/aml-policy'
    }
  ].map((item) => ({ ...item, icon: exportIcon }))
);

function goBack(): void {
  replace({ name: RouteNames.Settings });
}
</script>

<template>
  <section class="legal-view">
    <InnerLayout :title="t('legal.title')" :button-type="InnerLayoutButtonType.Back" @back="goBack">
      <div class="legal-view__inner">
        <ul class="legal-view__list">
          <template v-for="item in items" :key="item.name">
            <li class="legal-view__item">
              <UiLink
                class="legal-view__navigation-link"
                :icon="item.icon"
                :icon-size="24"
                :gap="16"
                weight="medium"
                :href="item.href"
                target="_blank"
              >
                {{ item.name }}

                <template #postfix>
                  <UiIcon class="legal-view__arrow" :name="arrowRightSmallIcon" :size="16" muted />
                </template>
              </UiLink>
            </li>
          </template>
        </ul>
      </div>
    </InnerLayout>
  </section>
</template>

<style scoped>
.legal-view__inner {
  padding: 24px 0 0;
}

.legal-view__list {
  margin: 0;
  padding: 16px;
  list-style-type: none;
  background-color: var(--color-main-underlay);
  border-radius: 12px;
}

.legal-view__item {
  display: flex;
}

.legal-view__item:not(:last-child) {
  margin: 0 0 24px;
}

.legal-view__navigation-link {
  flex-grow: 1;
  padding: 8px 0;
}

.legal-view__arrow {
  flex-shrink: 0;
  margin: 0 0 0 auto;
}

.legal-view__link {
  color: var(--color-main-text);
}
</style>
